import { Helmet } from 'react-helmet-async';
import { optimizeImageUrl } from '@/lib/imageUtils';

const BASE_URL = 'https://modernnomadliving.com';
const DEFAULT_OG_IMAGE = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&h=630&fit=crop';

function normalizeImageUrl(url?: string): string {
  if (!url) return DEFAULT_OG_IMAGE;
  const absoluteUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`;
  return optimizeImageUrl(absoluteUrl, 1200);
}

interface SEOHeadProps {
  title: string;
  description: string;
  path: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  modifiedTime?: string;
  image?: string;
  robots?: string;
}

export const SEOHead = ({
  title,
  description,
  path,
  type = 'website',
  publishedTime,
  modifiedTime,
  image,
  robots = 'index, follow',
}: SEOHeadProps) => {
  const fullTitle = `${title} - Modern Nomad Living`;
  const canonicalUrl = `${BASE_URL}${path}`;
  const ogImage = normalizeImageUrl(image);

  return (
    <Helmet>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content={robots} />
      
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="Modern Nomad Living" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
};
